/**
 * Enhanced Export Format Dropdown with full CSV, Excel, and PDF support
 * Replaces the old ExportFormatDropdown component
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import csvIcon from '../../static/icons/csv-icon.svg';
import xlsIcon from '../../static/icons/xls-icon.svg';
import pdfIcon from '../../static/icons/pdf-insight-icon.svg';
import { useDownload } from './DownloadManager';

const getExportFormats = () => [
  { key: 'csv', label: 'CSV (.csv)', icon: csvIcon, ext: 'csv' },
  { key: 'xlsx', label: 'Excel (.xlsx)', icon: xlsIcon, ext: 'xlsx' },
  { key: 'pdf', label: 'PDF (.pdf)', icon: pdfIcon, ext: 'pdf' },
];

/**
 * ExportFormatDropdown Component
 *
 * @param {Object} props
 * @param {string} props.section - Section identifier (from SECTIONS constant)
 * @param {*} props.data - Section data to export (used for Excel)
 * @param {*} props.csvData - Data specifically for CSV export (optional, defaults to data)
 * @param {Object} props.chartRef - React ref to Chart.js component (for PDF export)
 * @param {Object} props.allData - All data for full report export (optional)
 * @param {boolean} props.isFullReport - Whether this is a full report export
 * @param {boolean} props.isModal - Whether exporting from modal view
 * @param {Object} props.filterData - Filter data for PDF header
 * @param {string[]} props.excludedFormatKeys - Export formats to hide from the menu
 * @param {React.ReactElement} props.trigger - Trigger element (button)
 */
export function ExportFormatDropdown({
  section,
  data,
  csvData,
  chartRef,
  allData,
  isFullReport = false,
  isModal = false,
  filterData,
  excludedFormatKeys = [],
  trigger,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [hoveredKey, setHoveredKey] = useState(null);

  const { handleExport, loading, loadingFormat } = useDownload({
    section,
    data,
    // Use csvData if it's a non-empty array, otherwise fall back to data
    csvData: (Array.isArray(csvData) && csvData.length > 0) ? csvData : data,
    chartRef,
    allData,
    isFullReport,
    isModal,
    filterData,
  });

  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const triggerEl = React.isValidElement(trigger)
    ? React.cloneElement(trigger, {
        onClick: (e) => {
          trigger.props.onClick?.(e);
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        },
        disabled: loading,
      })
    : trigger;

  const handleFormatClick = async (format) => {
    setOpen(false);
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    await handleExport(format.key);
  };

  const exportFormats = getExportFormats().filter(
    (format) => !excludedFormatKeys.includes(format.key)
  );

  return (
    <Wrap ref={wrapRef}>
      {triggerEl}
      {open ? (
        <Menu role="menu" aria-label="Export format">
          {exportFormats.map((format) => (
            <MenuItem
              key={format.key}
              role="menuitem"
              aria-label={format.label}
              $hovered={hoveredKey === format.key}
              $loading={loading && loadingFormat === format.key}
              onMouseEnter={() => setHoveredKey(format.key)}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={() => handleFormatClick(format)}
            >
              <Icon src={format.icon} alt="" />
              <Text>
                {format.label}
                {loading && loadingFormat === format.key && (
                  <LoadingText> (Generating...)</LoadingText>
                )}
              </Text>
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const Menu = styled.div.attrs({
  'data-export-format-menu': 'true',
})`
  position: absolute;
  top: calc(100% + 12px);
  bottom: auto;
  right: 0;
  width: fit-content;
  min-width: 200px;
  max-width: min(92vw, 360px);
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e0eb;
  box-shadow: 0px 16px 40px rgba(0, 0, 0, 0.14);
  padding: 8px 10px;
  z-index: 2000;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  transition: background 0.15s;
  width: 100%;
  box-sizing: border-box;
  background: ${({ $hovered, $loading }) =>
    $loading ? '#f0f0f0' : $hovered ? '#f8f4fc' : 'transparent'};
  opacity: ${({ $loading }) => ($loading ? 0.6 : 1)};
  pointer-events: ${({ $loading }) => ($loading ? 'none' : 'auto')};
`;

const Icon = styled.img`
  width: 30px;
  height: 36px;
  flex-shrink: 0;
`;

const Text = styled.span`
  font-size: 16px;
  line-height: 1.2;
  font-weight: 400;
  color: #4b464f;
  white-space: nowrap;
`;

const LoadingText = styled.span`
  font-size: 14px;
  color: #7c3aed;
  font-style: italic;
`;
